import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, ElementRef, HostBinding, Input, OnDestroy, Optional, Self, OnInit } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NgControl, ValidatorFn } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-civility-radio-button',
    templateUrl: './civility-radio-button.component.html',
    styleUrls: ['./civility-radio-button.component.scss'],
    providers: [
        { provide: MatFormFieldControl, useExisting: CivilityRadioButtonComponent }
    ]
})
export class CivilityRadioButtonComponent implements OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string> {

    static nextId = 0;
    @Input() isCreation = false;
    @HostBinding() id: string = `civility-radio-button-${CivilityRadioButtonComponent.nextId++}`;
    @HostBinding('attr.aria-describedby') describedBy = '';

    stateChanges: Subject<void> = new Subject<void>();
    focused: boolean = false;
    disabled: boolean = false;
    defaultCheck: boolean;

    private _value = null;
    private _placeholder: string = '';
    private _required: boolean = false;

    constructor(
        @Optional() @Self() public ngControl: NgControl,
        private fm: FocusMonitor,
        private elRef: ElementRef<HTMLElement>
    ) {
        this.fm.monitor(this.elRef, true).subscribe(origin => {
            if (this.focused && !origin) {
                this.onTouched();
            }
            this.focused = !!origin;
            this.stateChanges.next();
        });
        if (this.ngControl != null) { // it replaces the NG_VALUE_ACCESSOR
            this.ngControl.valueAccessor = this;
        }
    }
    ngOnInit(): void {
        if (!this.getFormControl().value) {
            this.getFormControl().setValue(this.getNotDefinedValue());
        }
    }

    @HostBinding('class.floating') get shouldLabelFloat() { return this.focused || !this.empty; }

    @Input() // value
    get value(): string | null { return this._value; }
    set value(val: string | null) {
        this._value = val;
        this.onChange(this._value);
        this.stateChanges.next();
    }

    @Input() // placeholder
    get placeholder(): string { return this._placeholder; }
    set placeholder(placeHolder: string) {
        this._placeholder = placeHolder;
        this.stateChanges.next();
    }

    @Input() // required
    get required(): boolean { return this._required; }
    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }

    get empty(): boolean {
        return !this.value;
    }

    get errorState() {
        return this.ngControl.errors !== null && !!this.ngControl.touched;
    }

    get input(): HTMLInputElement {
        return this.elRef.nativeElement.querySelector('input');
    }

    ngOnDestroy() {
        this.stateChanges.complete();
        this.fm.stopMonitoring(this.elRef);
    }

    onChange = (_: any) => { };
    onTouched = () => { };

    writeValue(value: string): void {
        if (value) {
            this.value = value;
        }
    }
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join(' ');
    }
    onContainerClick(event: MouseEvent) {
        if ((event.target as Element).tagName.toLowerCase() !== 'input') {
            this.input.focus();
        }
    }
    getFormControl(): UntypedFormControl {
        return this.ngControl.control as UntypedFormControl;
    }

    getNotDefinedValue() {
        return this.isCreation ? undefined : null;
    }
}
